import { Injectable } from '@angular/core';
import { DataAccessService } from './data-access.service';
import { LockStatuses } from '../../constants/LockStatuses';
import { LockingStrategy, Lock, UnlockingStrategy, EditingStrategy, ServerLockDetails } from '../interfaces/lockable';
import { GlobalIntervalService } from './global-interval-service.service';
import { TranslationService } from './translation-service.service';

@Injectable()
export class LockService {

    refreshDuration: number = 3000;

    constructor(private dataAccess: DataAccessService, private globalInterval: GlobalIntervalService) {

    }

    async showLockedBy(lock: Lock, item: any = null) {

        var lockDetails = lock.lastLockDetails;
        if (item !== null)
            lockDetails = item.lastLockDetails;

        this.dataAccess.showErrorMessage('', this.generateLockedByMessage(lockDetails, false));
    }

    async showExpired(lock:Lock) {
        this.dataAccess.showErrorMessage('', lock.lockableProps.getTranslationService().translate('App.General.UI.ItemExpired', "This item has been modified on another session and this version is expired.", []));
    }

    async edit(lock: Lock, overwrite: boolean) {
        if (lock.goingToEdit)
            return;

        lock.destroyed = false;
        lock.goingToEdit = true;

        lock.lockToken = null; //The server will generate a token when null is sent
        //`${this.dataAccess.UserLoginId}${new Date().getTime()}`;

        this.processItemLock(lock, LockStatuses.Locked, overwrite, true);
    }

    async processItemLock(lock: Lock, lockMode: LockStatuses, overwrite: boolean, showMessages: boolean) {

        if (lock.lockableProps.lockingStrategy() === LockingStrategy.NoLocking && lock.goingToEdit && lockMode === LockStatuses.Locked) {
            lock.viewMode = false;
            lock.goingToEdit = false;
            return;
        }

        //Unlock Requests should not be destroyed.
        if (lock.destroyed && lockMode !== LockStatuses.NotLocked) {
            console.log('destroyed')
            return;
        }

        if (lock.lastLockRequest != null)
            lock.lastLockRequest.unsubscribe();

        var ts = lock.lockableProps.getTranslationService();

        lock.lastLockRequest = lock.lockableProps.invokeLockingEndpoint(lock, lockMode, overwrite).subscribe(async (x) => {
            this.processLockResponse(lock, lockMode, ts, overwrite, showMessages, x);
        }, (x) => {

            //If connection drops after the item has been locked, the lock icon and offline icon appear. The below line "fixes" this
            lock.locked = false;


            lock.goingToEdit = false;
            //Called when exiting. No need to do anything here
            if (lockMode === LockStatuses.NotLocked)
                return;

            if (lock.viewMode && showMessages) {
                this.dataAccess.dismissServerLoading();
                this.dataAccess.showErrorMessage(ts.translate('App.General.UI.Error', 'Error', []), 'Sorry, something went wrong on the server. This might be a connection issue. Or there could be a more serious issue.');
            }
            else {
                lock.noConnection = true;
            }

            setTimeout(() => {
                this.processItemLock(lock, lockMode, false, false);
            }, this.refreshDuration);
        });
    }

    processLockResponse(lock: Lock, lockMode: LockStatuses, ts: TranslationService, overwrite: boolean, showMessages: boolean, x: any) {
        //Called when exiting
        if (lockMode === LockStatuses.NotLocked) {
            //lock.destroyed = true;
            return;
        }

        lock.noConnection = false;
        lock.expired = false;

        //Edit Button Was Pressed
        if (lock.viewMode)
            lock.goingToEdit = false;

        if (!lock.saving) {
            Object.keys(x).forEach(async (itemId) => {
                var lockCheck = x[itemId] as ServerLockDetails;

                lock.lockToken = lockCheck.LockToken;

                var item = lock.lockableProps.locateLockableItem(itemId);

                //This is required when LockingStrategy = LockItemsOnList. Otherwise the component can return NULL.
                if (item === null)
                    item = {};

                lock.lastLockDetails = lockCheck;
                item.lastLockDetails = lockCheck;

                lock.locked = false;
                item.locked = false;

                //If the strategy is 'LockItemsOnList' then the form has to go to edit mode in all the below cases (NotLocked, Locked, and Expired).
                //Then the logic is applied for each item individually
                if (lock.lockableProps.lockingStrategy() === LockingStrategy.LockItemsOnList && lock.viewMode) {
                    lock.viewMode = false;
                }

                if (lockCheck.LockStatus === LockStatuses.NotLocked) {
                    //if (lock.lockInterval == null) {
                    //    lock.viewMode = false;
                    //    lock.lockInterval = this.globalInterval._5SecondTick.subscribe((x) => {
                    //        this.processItemLock(lock, lockMode, false, false);
                    //    });
                    //}

                    if (lock.viewMode)
                        lock.viewMode = false;

                    //setTimeout(() => {
                    //    this.processItemLock(lock, lockMode, false, false);
                    //}, this.refreshDuration);
                }
                else if (lockCheck.LockStatus === LockStatuses.Locked) {

                    if (lock.viewMode && showMessages && lock.lockableProps.lockingStrategy() === LockingStrategy.LockEntireForm) {
                        this.dataAccess.showErrorMessage(ts.translate('App.General.UI.Error', 'Error', []),
                            this.generateLockedByMessage(lockCheck, true),
                            ts.translate('App.General.UI.Ok', 'Ok', []),
                            lockCheck.AllowOverwrite ? [
                                {
                                    text: ts.translate('App.General.UI.Overwrite', 'Overwrite', []),
                                    //role: 'cancel',
                                    handler: (x) => {
                                        this.edit(lock, true);
                                    }
                                }
                            ] : []
                        );

                        //To interrupt the refresh
                        lock.destroyed = true;
                    }
                    else {
                        if (lock.lockableProps.lockingStrategy() === LockingStrategy.LockEntireForm) {
                            lock.locked = true;
                        }
                        else {
                            item.locked = true;
                        }
                    }

                    //if (!lock.viewMode) {
                    //    setTimeout(() => {
                    //        this.processItemLock(lock, lockMode, false, false);
                    //    }, this.refreshDuration);
                    //}
                }
                else if (lockCheck.LockStatus === LockStatuses.ExpiredVersion) {
                    if (lock.viewMode && showMessages && lock.lockableProps.lockingStrategy() === LockingStrategy.LockEntireForm) {
                        await this.dataAccess.showErrorMessage(ts.translate('App.General.UI.Error', 'Error', []),
                            `This Item has been modified after the last time you've viewed it. Please refresh it.`
                        );

                        //To interrupt the refresh
                        lock.destroyed = true;
                    }
                    else {
                        if (lock.lockableProps.lockingStrategy() === LockingStrategy.LockEntireForm) {
                            lock.expired = true;
                            //if (lock.lockInterval !== null)
                            //    lock.lockInterval.unsubscribe();


                            //To interrupt the refresh
                            lock.destroyed = true;
                        }
                        else {
                            item.expired = true;
                        }
                    }
                }
            });
        }

        if (!lock.viewMode && !lock.destroyed) {
            setTimeout(() => {
                this.processItemLock(lock, lockMode, false, false);
            }, this.refreshDuration);
        }
    }

    generateLockedByMessage(lockCheck: ServerLockDetails, showOverwriteButton): string {

        const zeroPad = (num, places) => String(num).padStart(places, '0')
        var expireInSeconds = lockCheck.SecondsRemainingToRelease;
        var expireTime = zeroPad(Math.floor(expireInSeconds / 60), 2) + ':' + zeroPad((expireInSeconds % 60), 2);

        return `This Item is Locked by (${lockCheck.LockedByUserFulleName})` +
            (lockCheck.SecondsRemainingToRelease ? `. The lock will be expired in (${expireTime})` : '') +
            (showOverwriteButton && lockCheck.AllowOverwrite ? `<br/>You can overwrite this lock by clicking 'Overwrite' button` : '');
    }

    editDisabled(lock: Lock, item:any = null): boolean {
        return lock.viewMode || (lock.locked || (item !== null && item.locked)) || lock.noConnection || (lock.expired || (item !== null && item.expired));
    }

    /**Make Sure this is called after processing the response. Because the loaded (And processed) data will be used in the lock functions */
    afterItemLoadAndResponseProcess(lock: Lock, response: any) {
        if (lock.lockableProps.editingStrategy() === EditingStrategy.OnEditButtonPress) {
            lock.viewMode = true;
        }
        else if (lock.lockableProps.editingStrategy() === EditingStrategy.AfterLoading) {
            lock.viewMode = true;

            var success = response['error'] === null || response['error'] === undefined;

            if (success) {
                if (response['Lock'])
                    this.processLockResponse(lock, LockStatuses.Locked, lock.lockableProps.getTranslationService(), false, false, response['Lock']);
                else
                    this.edit(lock, false);
            }
        }
    }

    /**Pass 'data' parameter to inject the payload to it. Or leave it if you need the payload to be returned */
    construct_LockPropsPayload(lock:Lock, data: any = null): any {
        var payload = lock.getLockablePropsPayload();

        if (data === null)
            return payload;

        data['LockProps'] = payload;

        return null;
    }

    beforeSave(lock: Lock) {
        lock.saving = true;
        //if (lock.lastLockRequest !== null)
        //    lock.lastLockRequest.unsubscribe();
    }

    afterSave(lock: Lock, response:any) {
        lock.saving = false;

        if (response) {
            var success = response['error'] === null || response['error'] === undefined;

            if (success && lock.lockableProps.unlockingStrategy() === UnlockingStrategy.AfterSave) {
                lock.destroyed = true; //Double check this
                lock.viewMode = true;
            }
        }
    }

    onExit(lock: Lock) {
        lock.destroyed = true;
        this.processItemLock(lock, LockStatuses.NotLocked, false, false);
    }
}